// Development entry point for Vite dev server
import '@/set-public-path';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from '@/App';
import { AppThemeProvider } from '@/theme/ColorModeContext';
import { ErrorBoundaryWrapper } from '@/components/modules/error/ErrorBoundaryWrapper';
import '@/assets/i18n';
import { Auth0Provider } from '@auth0/auth0-react';
import { env } from '@/lib/env';
import { ROUTES } from '@/constants/route-config';
import { getLocalizedPath } from '@/utils/locale';

/**
 * This file is the entry point for local development with Vite.
 * It directly renders the App component to the DOM without single-spa.
 *
 * For production and integration with single-spa, see single-spa.tsx
 */

import { createQueryClient } from '@/api/queryClient';
const queryClient = createQueryClient();

const rootElement = document.getElementById('root');

// Ensure the root element exists
if (!rootElement) {
  throw new Error(
    'Root element not found. Make sure there is a div with id "root" in your HTML.',
  );
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundaryWrapper
        onReset={() => {
          queryClient.invalidateQueries();
        }}
      >
        <BrowserRouter>
          <Auth0Provider
            domain={env.VITE_PUBLIC_AUTH0_DOMAIN}
            clientId={env.VITE_PUBLIC_AUTH0_CLIENT_ID}
            authorizationParams={{
              redirect_uri: window.location.origin,
              scope: 'openid profile email offline_access',
            }}
            cacheLocation="localstorage"
            useRefreshTokens
            skipRedirectCallback={false}
            onRedirectCallback={(appState) => {
              const targetUrl =
                appState?.returnTo ?? getLocalizedPath(ROUTES.DASHBOARD);
              window.location.href = targetUrl;
            }}
          >
            <AppThemeProvider>
              <App />
            </AppThemeProvider>
          </Auth0Provider>
        </BrowserRouter>
      </ErrorBoundaryWrapper>
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-right'/>
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);

// For development hot module replacement
if (import.meta.hot) {
  import.meta.hot.accept();
}
